import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { EventLocation } from './event-location'
import { PLACEHOLDER_EVENT_LOCATION } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.models'

/**
 * Service for handling event location operations including formatting,
 * parsing, and creating EventLocation objects from various sources.
 */
@Injectable({ providedIn: 'root' })
export class EventLocationService {
  constructor(private translateService: TranslateService) {}

  /**
   * Formats an EventLocation object for display in form fields.
   * Returns empty string if no address is present.
   * If both name and address exist, returns "Name, Address", otherwise just the address.
   *
   * @param eventLocation - The EventLocation object to format
   * @returns Formatted string representation of the location
   */
  formatEventLocation(eventLocation: EventLocation): string {
    if (!eventLocation?.address) return ''
    return eventLocation.name ? `${eventLocation.name}, ${eventLocation.address}` : eventLocation.address
  }

  /**
   * Creates an EventLocation object from a Google Maps Place autocomplete result.
   * Extracts name (if it's a business), formatted address, and coordinates.
   *
   * @param result - The Google Maps Place result object
   * @returns A new EventLocation instance with the extracted data
   */
  createEventLocationFromPlaceResult(result: any): EventLocation {
    return new EventLocation(
      'business_status' in result ? result.name : '',
      result.formatted_address,
      result.geometry.location.lat(),
      result.geometry.location.lng(),
    )
  }

  /**
   * Parses a string input into an EventLocation object.
   * Used when users manually enter location text without using autocomplete.
   *
   * @param value - The string value to parse
   * @returns A new EventLocation with the value as the address
   */
  parseEventLocation(value: string): EventLocation {
    return new EventLocation('', value)
  }

  /**
   * Creates an EventLocation with "TBC" (To Be Confirmed) as the address.
   * Uses the translated version of "TBC" based on the current language.
   *
   * @returns A new EventLocation with translated "TBC" text
   */
  createTBCLocation(): EventLocation {
    return new EventLocation('', this.translateService.instant(PLACEHOLDER_EVENT_LOCATION))
  }
}
